import { useContext, useState } from "react";
import api from "../../api";
import { useToast } from "../ToastContext";
import { LoadingContext } from "../LoadingContext";
import { AuthContext } from "../AuthContext";

export interface ContratoStatusReportType {
    contratoId: number;
    description?: string;
    class?: string;
    instituicaoId: number;
    instituicaoName?: string;
    city?: string;
    state?: string;
    cursos: string[];
    hiredStudents?: number;
    currentStudents?: number;
    creationDate?: string;
    completionDate?: string;
    dateOfDischarge?: string;
    statusConsolidado: number;
    statusConsolidadoLabel: string;
    editorId?: number;
    editorName?: string;
    laboratorioId?: number;
    laboratorioName?: string;
    editorDeliveryDate?: string;
    editorReturnDate?: string;
    laboratoryDeliveryDate?: string;
    laboratoryReturnDate?: string;
}

export interface ContratoStatusReportFilters {
    EmpresaId?: number;
    InstituicaoId?: number;
    ContratoId?: number;
    Description?: string;
    City?: string;
    State?: string;
    Region?: string;
    IsPrivate?: boolean;
    SomenteKit?: boolean;
    Status?: number;
    StartDate?: string;
    EndDate?: string;
    StatusConsolidado?: number;
    EditorId?: number;
    LaboratorioId?: number;
    VendedorId?: number;
}

export default function useContratoStatusReport() {
    const showToast = useToast();
    const { setLoading } = useContext(LoadingContext);
    const { handleLogout } = useContext(AuthContext);
    const [listContratoStatusReport, setListContratoStatusReport] = useState<ContratoStatusReportType[]>([]);
    const [contratoStatusReportPageCount, setContratoStatusReportPageCount] = useState<number>(0);

    function buildParams(filters: ContratoStatusReportFilters, pageNumber?: number, pageSize?: number) {
        const params: Record<string, any> = {};

        if (pageNumber) params.pageNumber = pageNumber;
        if (pageSize) params.pageSize = pageSize;
        if (filters.EmpresaId) params.EmpresaId = filters.EmpresaId;
        if (filters.InstituicaoId) params.InstituicaoId = filters.InstituicaoId;
        if (filters.ContratoId) params.ContratoId = filters.ContratoId;
        if (filters.Description) params.Description = filters.Description;
        if (filters.City) params.City = filters.City;
        if (filters.State) params.State = filters.State;
        if (filters.Region) params.Region = filters.Region;
        if (filters.IsPrivate !== undefined) params.IsPrivate = filters.IsPrivate;
        if (filters.SomenteKit !== undefined) params.SomenteKit = filters.SomenteKit;
        if (filters.Status !== undefined && filters.Status !== 0) params.Status = filters.Status;
        if (filters.StartDate) params.StartDate = filters.StartDate;
        if (filters.EndDate) params.EndDate = filters.EndDate;
        if (filters.StatusConsolidado) params.StatusConsolidado = filters.StatusConsolidado;
        if (filters.EditorId) params.EditorId = filters.EditorId;
        if (filters.LaboratorioId) params.LaboratorioId = filters.LaboratorioId;
        if (filters.VendedorId) params.VendedorId = filters.VendedorId;

        return params;
    }

    function getContratoStatusReportPaged(
        pageNumber: number,
        pageSize: number,
        filters: ContratoStatusReportFilters
    ): Promise<ContratoStatusReportType[] | null> {
        setLoading(true);

        return api
            .get(`/Contrato/status-report`, { params: buildParams(filters, pageNumber, pageSize) })
            .then((response) => {
                setListContratoStatusReport(response.data.result);
                setContratoStatusReportPageCount(response.data.pageCount);
                setLoading(false);
                return response.data.result;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                } else {
                    showToast("error", "Erro ao carregar relatório de status dos contratos. Verifique novamente mais tarde.", error);
                }
                setLoading(false);
                console.error("Erro ao carregar relatório de status dos contratos:", error);
                return null;
            });
    }

    function exportContratoStatusReportPdf(filters: ContratoStatusReportFilters): Promise<void> {
        setLoading(true);

        return api
            .get(`/Contrato/status-report/export/pdf`, {
                params: buildParams(filters),
                responseType: "blob",
            })
            .then((response) => {
                const blob = new Blob([response.data], { type: "application/pdf" });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", "relatorio-status-contratos.pdf");
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                setLoading(false);
            })
            .catch((error) => {
                if (error?.response?.status === 404) {
                    showToast("error", "Nenhum contrato encontrado para os filtros selecionados.");
                } else {
                    showToast("error", "Erro ao exportar o PDF. Tente novamente mais tarde.", error);
                }
                console.error("Erro ao exportar PDF:", error);
                setLoading(false);
            });
    }

    function exportContratoStatusReportExcel(filters: ContratoStatusReportFilters): Promise<void> {
        setLoading(true);

        return api
            .get(`/Contrato/status-report/export/excel`, {
                params: buildParams(filters),
                responseType: "blob",
            })
            .then((response) => {
                const blob = new Blob([response.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", "relatorio-status-contratos.xlsx");
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                setLoading(false);
            })
            .catch((error) => {
                if (error?.response?.status === 404) {
                    showToast("error", "Nenhum contrato encontrado para os filtros selecionados.");
                } else {
                    showToast("error", "Erro ao exportar o Excel. Tente novamente mais tarde.", error);
                }
                console.error("Erro ao exportar Excel:", error);
                setLoading(false);
            });
    }

    return {
        listContratoStatusReport,
        contratoStatusReportPageCount,
        setListContratoStatusReport,
        setContratoStatusReportPageCount,
        getContratoStatusReportPaged,
        exportContratoStatusReportPdf,
        exportContratoStatusReportExcel,
    };
}
